import React, { useEffect, useMemo, useState } from "react";
import CustomSelect from "../../../components/CustomSelect.jsx";
import {
    activateAdminSeason,
    archiveAdminSeason,
    createAdminRace,
    createAdminSeason,
    deleteAdminRace,
    fetchAdminSeasonRaces,
    fetchAdminSeasons,
    fetchManagerSeriesSeasons,
    updateAdminRace,
    updateAdminSeason,
} from "../../../lib/managerApi.js";

const SEASON_STATUS_OPTIONS = [
    { value: "draft", label: "Draft" },
    { value: "active", label: "Aktiv" },
    { value: "archived", label: "Archiviert" },
];

const RACE_STATUS_OPTIONS = [
    { value: "scheduled", label: "Geplant" },
    { value: "done", label: "Erledigt" },
    { value: "cancelled", label: "Abgesagt" },
];

const EMPTY_SEASON = {
    season_key: "",
    season_no: "",
    display_name: "",
    status: "draft",
    max_regular_starters: "60",
    start_date: "",
    end_date: "",
};

const EMPTY_RACE = {
    race_no: "",
    race_tag: "",
    track_name: "",
    race_title: "",
    race_date: "",
    briefing_from: "",
    results_from: "",
    sort_order: "0",
    status: "scheduled",
};

function prettyError(err) {
    if (!err) return "Unbekannter Fehler";
    if (typeof err === "string") return err;
    if (err instanceof Error) return err.message || "Fehler";
    if (err.detail) return typeof err.detail === "string" ? err.detail : JSON.stringify(err.detail);
    return String(err);
}

function dateTimeInput(value) {
    if (!value) return "";
    return String(value).slice(0, 16);
}

function statusLabel(status) {
    if (status === "active") return "Aktiv";
    if (status === "archived") return "Archiviert";
    if (status === "done") return "Erledigt";
    if (status === "cancelled") return "Abgesagt";
    if (status === "scheduled") return "Geplant";
    return "Draft";
}

function StatusPill({ status }) {
    const cls = status === "active" || status === "scheduled"
        ? "pill pill--am"
        : status === "archived" || status === "done"
            ? "pill pill--gesamt"
            : "pill pill--int";

    return <span className={cls}>{statusLabel(status)}</span>;
}

function Field({ label, children, hint = "", required = false, className = "" }) {
    return (
        <label className={`season-field ${className}`.trim()}>
            <span className="season-field__label">
                {label}
                {required && <span aria-hidden="true"> *</span>}
            </span>
            {children}
            {hint && <span className="season-field__hint">{hint}</span>}
        </label>
    );
}

export default function SeasonManagement() {
    const [seriesRows, setSeriesRows] = useState([]);
    const [seriesKey, setSeriesKey] = useState("gt3x");
    const [seasons, setSeasons] = useState([]);
    const [selectedSeasonId, setSelectedSeasonId] = useState("");
    const [seasonForm, setSeasonForm] = useState(EMPTY_SEASON);
    const [creatingSeason, setCreatingSeason] = useState(false);

    const [races, setRaces] = useState([]);
    const [editingRaceId, setEditingRaceId] = useState(null);
    const [raceForm, setRaceForm] = useState(EMPTY_RACE);

    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const seriesOptions = useMemo(() => {
        return seriesRows.map((series) => ({
            value: series.series_key,
            label: series.display_name,
        }));
    }, [seriesRows]);

    const selectedSeason = useMemo(() => {
        return seasons.find((season) => String(season.id) === String(selectedSeasonId)) || null;
    }, [seasons, selectedSeasonId]);

    useEffect(() => {
        let active = true;

        async function loadSeries() {
            try {
                setLoading(true);
                setError("");
                const data = await fetchManagerSeriesSeasons();
                if (!active) return;

                const rows = Array.isArray(data.series) ? data.series : [];
                setSeriesRows(rows);
                const preferred = rows.find((series) => series.series_key === "gt3x") || rows[0];
                if (preferred) setSeriesKey(preferred.series_key);
            } catch (err) {
                if (active) setError(prettyError(err));
            } finally {
                if (active) setLoading(false);
            }
        }

        loadSeries();
        return () => {
            active = false;
        };
    }, []);

    async function loadSeasons(nextSeriesKey = seriesKey) {
        if (!nextSeriesKey) return;

        try {
            setLoading(true);
            setError("");
            const data = await fetchAdminSeasons({ series_key: nextSeriesKey });
            const rows = Array.isArray(data.seasons) ? data.seasons : [];
            setSeasons(rows);

            setSelectedSeasonId((current) => {
                if (current && rows.some((season) => String(season.id) === String(current))) return current;
                const activeSeason = rows.find((season) => season.status === "active") || rows[0];
                return activeSeason?.id ? String(activeSeason.id) : "";
            });
        } catch (err) {
            setError(prettyError(err));
            setSeasons([]);
            setSelectedSeasonId("");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (!seriesKey) return;
        setCreatingSeason(false);
        loadSeasons(seriesKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seriesKey]);

    useEffect(() => {
        if (creatingSeason) {
            setSeasonForm(EMPTY_SEASON);
            return;
        }

        if (!selectedSeason) {
            setSeasonForm(EMPTY_SEASON);
            return;
        }

        setSeasonForm({
            season_key: selectedSeason.season_key || "",
            season_no: selectedSeason.season_no ? String(selectedSeason.season_no) : "",
            display_name: selectedSeason.display_name || "",
            status: selectedSeason.status || "draft",
            max_regular_starters: selectedSeason.max_regular_starters ? String(selectedSeason.max_regular_starters) : "60",
            start_date: selectedSeason.start_date || "",
            end_date: selectedSeason.end_date || "",
        });
    }, [selectedSeason, creatingSeason]);

    async function loadRaces() {
        if (!selectedSeasonId) {
            setRaces([]);
            return;
        }

        try {
            setError("");
            const data = await fetchAdminSeasonRaces(selectedSeasonId);
            setRaces(Array.isArray(data.races) ? data.races : []);
        } catch (err) {
            setError(prettyError(err));
            setRaces([]);
        }
    }

    useEffect(() => {
        loadRaces();
        resetRaceForm();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSeasonId]);

    function resetRaceForm() {
        setEditingRaceId(null);
        setRaceForm(EMPTY_RACE);
    }

    function startRaceEdit(race) {
        setEditingRaceId(race.id);
        setRaceForm({
            race_no: race.race_no ? String(race.race_no) : "",
            race_tag: race.race_tag || "",
            track_name: race.track_name || "",
            race_title: race.race_title || "",
            race_date: dateTimeInput(race.race_date),
            briefing_from: dateTimeInput(race.briefing_from),
            results_from: dateTimeInput(race.results_from),
            sort_order: race.sort_order ? String(race.sort_order) : "0",
            status: race.status || "scheduled",
        });
    }

    function seasonPayload() {
        return {
            series_key: seriesKey,
            season_key: seasonForm.season_key,
            season_no: Number(seasonForm.season_no),
            display_name: seasonForm.display_name,
            status: seasonForm.status,
            max_regular_starters: Number(seasonForm.max_regular_starters || 60),
            start_date: seasonForm.start_date,
            end_date: seasonForm.end_date,
        };
    }

    function racePayload() {
        return {
            race_no: Number(raceForm.race_no),
            race_tag: raceForm.race_tag,
            track_name: raceForm.track_name,
            race_title: raceForm.race_title,
            race_date: raceForm.race_date,
            briefing_from: raceForm.briefing_from,
            results_from: raceForm.results_from,
            sort_order: Number(raceForm.sort_order || 0),
            status: raceForm.status,
        };
    }

    async function saveSeason() {
        if (!seasonForm.season_key || !seasonForm.season_no || !seasonForm.display_name) {
            setError("Bitte Season Key, Nummer und Namen ausfuellen.");
            return;
        }

        try {
            setBusy(true);
            setError("");
            setSuccess("");
            const payload = seasonPayload();
            const data = creatingSeason
                ? await createAdminSeason(payload)
                : await updateAdminSeason(selectedSeasonId, payload);

            setSuccess(creatingSeason ? "Saison wurde angelegt." : "Saison wurde gespeichert.");
            setCreatingSeason(false);
            await loadSeasons(seriesKey);
            if (data.season?.id) setSelectedSeasonId(String(data.season.id));
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    async function activateSeason() {
        if (!selectedSeasonId) return;

        try {
            setBusy(true);
            setError("");
            setSuccess("");
            await activateAdminSeason(selectedSeasonId);
            setSuccess("Saison wurde aktiviert. Andere aktive Saisons dieser Serie wurden archiviert.");
            await loadSeasons(seriesKey);
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    async function archiveSeason() {
        if (!selectedSeasonId) return;

        try {
            setBusy(true);
            setError("");
            setSuccess("");
            await archiveAdminSeason(selectedSeasonId);
            setSuccess("Saison wurde archiviert.");
            await loadSeasons(seriesKey);
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    async function saveRace() {
        if (!selectedSeasonId || !raceForm.race_no || !raceForm.race_tag || !raceForm.track_name) {
            setError("Bitte Race Nummer, Race Tag und Strecke ausfuellen.");
            return;
        }

        try {
            setBusy(true);
            setError("");
            setSuccess("");
            const payload = racePayload();
            if (editingRaceId) {
                await updateAdminRace(editingRaceId, payload);
                setSuccess("Rennen wurde gespeichert.");
            } else {
                await createAdminRace(selectedSeasonId, payload);
                setSuccess("Rennen wurde angelegt.");
            }
            resetRaceForm();
            await loadRaces();
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    async function removeRace(raceId) {
        if (!window.confirm("Rennen wirklich loeschen?")) return;

        try {
            setBusy(true);
            setError("");
            setSuccess("");
            await deleteAdminRace(raceId);
            setSuccess("Rennen wurde geloescht.");
            await loadRaces();
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="rv-page manager-modern-page season-management-page">
            <section className="rv-page-hero">
                <div className="rv-page-hero__inner">
                    <div>
                        <div className="rv-page-kicker">Admin</div>
                        <h1 className="rv-page-title">Saisonverwaltung</h1>
                        <p className="rv-page-subtitle">
                            Saisons anlegen, aktivieren, archivieren und den Schedule einer Saison pflegen.
                        </p>
                    </div>
                </div>
            </section>

            {error && <div className="rv-message rv-message--error">{error}</div>}
            {success && <div className="rv-message rv-message--ok">{success}</div>}

            <div className="card rv-filter-card" style={{ display: "grid", gap: 14 }}>
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                        gap: 12,
                    }}
                >
                    <Field label="Serie">
                        <CustomSelect
                            value={seriesKey}
                            onChange={setSeriesKey}
                            options={seriesOptions}
                            allowEmpty={false}
                        />
                    </Field>
                    <div style={{ display: "flex", gap: 10, alignItems: "end", flexWrap: "wrap" }}>
                        <button className="btn btn--accent" type="button" onClick={() => setCreatingSeason(true)} disabled={busy}>
                            Neue Saison
                        </button>
                        <button className="btn" type="button" onClick={() => loadSeasons(seriesKey)} disabled={loading}>
                            Aktualisieren
                        </button>
                    </div>
                </div>
            </div>

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 360px), 1fr))",
                    gap: 16,
                    alignItems: "start",
                }}
            >
                <div className="card" style={{ display: "grid", gap: 10 }}>
                    <h2 style={{ marginTop: 0 }}>Saisons</h2>
                    {!seasons.length && <div className="muted">Keine Saisons vorhanden.</div>}
                    {seasons.map((season) => {
                        const active = String(season.id) === String(selectedSeasonId) && !creatingSeason;
                        return (
                            <button
                                key={season.id}
                                type="button"
                                onClick={() => {
                                    setCreatingSeason(false);
                                    setSelectedSeasonId(String(season.id));
                                }}
                                style={{
                                    textAlign: "left",
                                    border: active
                                        ? "1px solid rgba(228,254,60,.46)"
                                        : "1px solid rgba(255,255,255,.10)",
                                    background: active
                                        ? "rgba(228,254,60,.10)"
                                        : "rgba(255,255,255,.035)",
                                    borderRadius: 14,
                                    padding: 12,
                                    color: "var(--text)",
                                    cursor: "pointer",
                                    display: "grid",
                                    gap: 6,
                                }}
                            >
                                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                                    <strong>{season.display_name}</strong>
                                    <StatusPill status={season.status} />
                                </div>
                                <div className="muted" style={{ fontSize: 13 }}>
                                    {season.season_key} / Season {season.season_no} / Starter {season.max_regular_starters}
                                </div>
                            </button>
                        );
                    })}
                </div>

                <div className="card" style={{ display: "grid", gap: 18 }}>
                    <section style={{ display: "grid", gap: 12 }}>
                        <div>
                            <div className="rv-page-kicker">{creatingSeason ? "Neue Saison" : "Saison bearbeiten"}</div>
                            <h2 style={{ margin: "4px 0 0" }}>
                                {creatingSeason ? "Saison anlegen" : selectedSeason?.display_name || "Keine Saison ausgewaehlt"}
                            </h2>
                        </div>

                        <div className="season-management-form-grid">
                            <Field
                                label="Season Key"
                                required
                                hint="Kurzkey fuer API/URLs, z. B. s3."
                            >
                                <input
                                    className="input"
                                    value={seasonForm.season_key}
                                    onChange={(event) => setSeasonForm((prev) => ({ ...prev, season_key: event.target.value }))}
                                    placeholder="z. B. s3"
                                />
                            </Field>

                            <Field label="Season Nummer" required hint="Muss pro Serie eindeutig sein.">
                                <input
                                    className="input"
                                    type="number"
                                    min="1"
                                    value={seasonForm.season_no}
                                    onChange={(event) => setSeasonForm((prev) => ({ ...prev, season_no: event.target.value }))}
                                />
                            </Field>

                            <Field label="Name" required className="season-field--wide">
                                <input
                                    className="input"
                                    value={seasonForm.display_name}
                                    onChange={(event) => setSeasonForm((prev) => ({ ...prev, display_name: event.target.value }))}
                                    placeholder="Season 3"
                                />
                            </Field>

                            <Field label="Status" hint="Aktivieren archiviert andere aktive Saisons dieser Serie.">
                                <CustomSelect
                                    value={seasonForm.status}
                                    onChange={(value) => setSeasonForm((prev) => ({ ...prev, status: value }))}
                                    options={SEASON_STATUS_OPTIONS}
                                    allowEmpty={false}
                                />
                            </Field>

                            <Field label="Max Starter" hint="Grenze fuer Regular-Grid in Abmeldungen.">
                                <input
                                    className="input"
                                    type="number"
                                    min="1"
                                    value={seasonForm.max_regular_starters}
                                    onChange={(event) => setSeasonForm((prev) => ({ ...prev, max_regular_starters: event.target.value }))}
                                />
                            </Field>

                            <Field label="Startdatum" hint="Optional">
                                <input
                                    className="input"
                                    type="date"
                                    value={seasonForm.start_date}
                                    onChange={(event) => setSeasonForm((prev) => ({ ...prev, start_date: event.target.value }))}
                                />
                            </Field>

                            <Field label="Enddatum" hint="Optional">
                                <input
                                    className="input"
                                    type="date"
                                    value={seasonForm.end_date}
                                    onChange={(event) => setSeasonForm((prev) => ({ ...prev, end_date: event.target.value }))}
                                />
                            </Field>
                        </div>

                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                            <button className="btn btn--accent" type="button" onClick={saveSeason} disabled={busy || (!creatingSeason && !selectedSeasonId)}>
                                {creatingSeason ? "Saison anlegen" : "Saison speichern"}
                            </button>
                            {!creatingSeason && selectedSeasonId && (
                                <>
                                    <button className="btn" type="button" onClick={activateSeason} disabled={busy}>
                                        Aktivieren
                                    </button>
                                    <button className="btn" type="button" onClick={archiveSeason} disabled={busy}>
                                        Archivieren
                                    </button>
                                </>
                            )}
                        </div>
                    </section>

                    {!creatingSeason && selectedSeasonId && (
                        <section style={{ display: "grid", gap: 12 }}>
                            <h3 style={{ margin: 0 }}>Schedule / Rennen</h3>

                            <div className="season-management-race-grid">
                                <Field label="Race Nummer" required>
                                    <input
                                        className="input"
                                        type="number"
                                        min="1"
                                        value={raceForm.race_no}
                                        onChange={(event) => setRaceForm((prev) => ({ ...prev, race_no: event.target.value }))}
                                    />
                                </Field>

                                <Field label="Race Tag" required hint="Kurzname, z. B. R1.">
                                    <input
                                        className="input"
                                        value={raceForm.race_tag}
                                        onChange={(event) => setRaceForm((prev) => ({ ...prev, race_tag: event.target.value }))}
                                        placeholder="R1"
                                    />
                                </Field>

                                <Field label="Strecke" required className="season-field--wide">
                                    <input
                                        className="input"
                                        value={raceForm.track_name}
                                        onChange={(event) => setRaceForm((prev) => ({ ...prev, track_name: event.target.value }))}
                                    />
                                </Field>

                                <Field label="Titel" className="season-field--wide" hint="Optionaler Anzeigename.">
                                    <input
                                        className="input"
                                        value={raceForm.race_title}
                                        onChange={(event) => setRaceForm((prev) => ({ ...prev, race_title: event.target.value }))}
                                        placeholder="Optional"
                                    />
                                </Field>

                                <Field label="Rennen ab">
                                    <input
                                        className="input"
                                        type="datetime-local"
                                        value={raceForm.race_date}
                                        onChange={(event) => setRaceForm((prev) => ({ ...prev, race_date: event.target.value }))}
                                    />
                                </Field>

                                <Field label="Briefing sichtbar ab" hint="Steuert Auto-Race-Zeitraum.">
                                    <input
                                        className="input"
                                        type="datetime-local"
                                        value={raceForm.briefing_from}
                                        onChange={(event) => setRaceForm((prev) => ({ ...prev, briefing_from: event.target.value }))}
                                    />
                                </Field>

                                <Field label="Results sichtbar ab" hint="Ende des Auto-Race-Zeitraums.">
                                    <input
                                        className="input"
                                        type="datetime-local"
                                        value={raceForm.results_from}
                                        onChange={(event) => setRaceForm((prev) => ({ ...prev, results_from: event.target.value }))}
                                    />
                                </Field>

                                <Field label="Sortierung">
                                    <input
                                        className="input"
                                        type="number"
                                        min="0"
                                        value={raceForm.sort_order}
                                        onChange={(event) => setRaceForm((prev) => ({ ...prev, sort_order: event.target.value }))}
                                    />
                                </Field>

                                <Field label="Status">
                                    <CustomSelect
                                        value={raceForm.status}
                                        onChange={(value) => setRaceForm((prev) => ({ ...prev, status: value }))}
                                        options={RACE_STATUS_OPTIONS}
                                        allowEmpty={false}
                                    />
                                </Field>
                            </div>

                            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                <button className="btn btn--accent" type="button" onClick={saveRace} disabled={busy}>
                                    {editingRaceId ? "Rennen speichern" : "Rennen anlegen"}
                                </button>
                                {editingRaceId && (
                                    <button className="btn" type="button" onClick={resetRaceForm} disabled={busy}>
                                        Bearbeitung abbrechen
                                    </button>
                                )}
                            </div>

                            <div style={{ display: "grid", gap: 8 }}>
                                {!races.length && <div className="muted">Noch keine Rennen fuer diese Saison.</div>}
                                {races.map((race) => (
                                    <div
                                        key={race.id}
                                        style={{
                                            border: "1px solid rgba(255,255,255,.10)",
                                            borderRadius: 14,
                                            padding: 12,
                                            background: "rgba(255,255,255,.035)",
                                            display: "grid",
                                            gap: 8,
                                        }}
                                    >
                                        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                                            <strong>{race.race_tag} - {race.track_name}</strong>
                                            <StatusPill status={race.status} />
                                        </div>
                                        <div className="muted" style={{ fontSize: 13 }}>
                                            Race #{race.race_no} / Sort {race.sort_order}
                                            {race.race_date ? ` / ${dateTimeInput(race.race_date).replace("T", " ")}` : ""}
                                        </div>
                                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                            <button className="btn" type="button" onClick={() => startRaceEdit(race)} disabled={busy}>
                                                Bearbeiten
                                            </button>
                                            <button className="btn" type="button" onClick={() => removeRace(race.id)} disabled={busy}>
                                                Loeschen
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}
                </div>
            </div>
        </div>
    );
}
